<template>
  <div class="view-container">
    <div class="extended-filter-block">
      <h2>Группы</h2>
      <div class="extended-search-block-mobile">
        <div class="search-outline-block">
          <SearchOutlined @click="showMobileFilterModal = true" style="color: rgba(0, 0, 0, 0.45)" />
          <div v-if="currentFiltersLine" class="dot"></div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="filter-block">
        <div class="add-button">
          <a-button @click="() => showModal('create')">
            <template #icon>
              <PlusOutlined />
            </template>
            Создать группу
          </a-button>
        </div>
      </div>

      <a-table
          rowKey="id"
          :columns="columns"
          :data-source="groupData"
          :scroll="{ x: scrollX }"
          :row-selection="{ selectedRowKeys: selectedGroups, onChange: onSelectChange, columnWidth: 18 }"
          :pagination="false"
          :loading="tableLoading"
          @change="handleTableChange"
          :table-layout="'fixed'"
      >
        <template #bodyCell="{ column, text, record }">
          <template v-if="column.dataIndex === 'name'">
            <a class="group-link" @click="router.push(`/group/overview/${record.id}`)">{{ text }}</a>
          </template>

          <template v-if="column.dataIndex === 'groups'">
            <a-tag v-for="(group) in record.groups" :key="group.id">
              {{ group.name }}
            </a-tag>
          </template>

          <template v-if="column.dataIndex === 'status'">
            <a-tag color="green" v-if="record.status === 'active'">
              Работает
            </a-tag>
            <a-tag color="red" v-else>
              В архиве
            </a-tag>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a-dropdown :trigger="['click']" placement="bottomLeft">
              <a-button type="text" @click.prevent>
                <MenuOutlined />
              </a-button>

              <template #overlay>
                <a-menu @click="({key}) => showModal(key, record)">
                  <a-menu-item key="manage_access">Управлять доступами</a-menu-item>
                  <a-menu-item key="copy">Создать копию</a-menu-item>
                  <a-menu-item key="delete">Удалить</a-menu-item>
                  <a-menu-item key="export">Экспортировать</a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </template>
        </template>

        <template #emptyText>
          <div class="no-data-wrapper">
            <img :src="NotFoundIcon" class="no-data-image" alt="Нет результатов"/>

            <div class="no-data-text">
              {{
                currentFiltersLine
                    ? 'Нет данных для отображения. Измените фильтры или запрос.'
                    : 'Нет данных для отображения. Добавьте группы, чтобы начать работу.'
              }}
            </div>

            <a-button
                v-if="currentFiltersLine"
                @click="resetFilters"
            >
              <template #icon>
                <ReloadOutlined />
              </template>
              Сбросить фильтры
            </a-button>

            <a-button
                v-else
                @click="() => showActionModal('create')"
            >
              <template #icon>
                <PlusOutlined />
              </template>
              Создать группу
            </a-button>
          </div>
        </template>
      </a-table>

      <div class="bottom-container">
        <div class="action-block">
          <div class="action-select-wrapper">
            <a-select
                v-model:value="actionToApply"
                :placeholder="'Выберите действие'"
                style="width: 100%;"
                @change="() => showModal(actionToApply)"
                :not-found-content="'Нет данных'"
                :disabled="!selectedGroups.length"
            >
              <template v-for="e in actions" :key="e.id">
                <a-select-option :disabled="e.name === 'Объединить' && selectedGroups.length < 2">
                  {{ e.name }}
                </a-select-option>
              </template>
            </a-select>
          </div>
        </div>
        <a-pagination v-model:current="currentPage"
                      :page-size="entitiesPerPage"
                      :buildOptionText="(count) => `${count.value} / стр`"
                      :show-total
                          ="total => `Всего ${total ?? 0} элементов`"
                      :total="groupsTotalCount"
                      @change="handlePageChange"
        />
      </div>
    </div>

    <GroupMobileFilterForm
      v-if="showMobileFilterModal"
      :open="showMobileFilterModal"
      @close="showMobileFilterModal = false"
    />

    <EditGroupForm
        v-if="currentAction === 'create'"
        :open="currentAction === 'create'"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentAction"
    />

    <MergeGroupsForm
        v-if="currentAction === 'merge'"
        :open="currentAction === 'merge'"
        :selectedGroups="selectedGroups"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentAction"
    />

    <CopyGroup
        v-if="currentAction === 'copy'"
        :open="currentAction === 'copy'"
        :currentGroupId="currentActionGroup.id"
        :name="(currentActionGroup.name + ' - ' + 'Копия')"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentAction"
    />

    <DeleteGroupForm
        v-if="currentAction === 'delete'"
        :open="currentAction === 'delete'"
        :currentGroup="currentActionGroup"
        :selectedGroups="selectedGroups"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentAction"
    />

    <NewOrderForm
        v-if="showOrderForm"
        :open="showOrderForm"
        @close="showOrderForm = false"
    />

    <ManageAccessForm
        v-if="currentAction === 'manage_access'"
        @close="clearCurrentAction"
        :userData="manageGroupAccessUsers"
        @showStaff="() => router.push('/staff')"
        @showOrderForm="() => {
              clearCurrentAction();
              showOrderForm = true;
            }"
        :name="currentActionGroup ? currentActionGroup.name : null"
        :mode="'group'"
        :open="currentAction === 'manage_access'"
    />
  </div>
</template>

<script>
import {onBeforeMount, onBeforeUnmount, reactive, ref, watch} from "@vue/runtime-core";
import {h, onMounted} from "vue";
import NotFoundIcon from "@/assets/images/icons/no_data.svg";
import {useStore} from "vuex";
import {useRouter} from "vue-router";
import {computed} from "@vue/reactivity";
import { SearchOutlined, SettingOutlined, PlusOutlined, ReloadOutlined, MenuOutlined, DownOutlined} from '@ant-design/icons-vue'
import ManageAccessForm from "@/components/Forms/Access/ManageAccessForm.vue";
import NewOrderForm from "@/views/Orders/OrderForm.vue";
import DeleteGroupForm from "@/feature/users/forms/DeleteGroupForm.vue";
import CopyGroup from "@/feature/group-control/forms/CopyGroup.vue";
import MergeGroupsForm from "@/feature/group-control/forms/MergeGroupsForm.vue";
import EditGroupForm from "@/feature/group-control/forms/EditGroupForm.vue";
import GroupMobileFilterForm from "@/feature/group-control/GroupMobileFilterForm.vue";
import {message} from "ant-design-vue";
import {useTableColumns} from "@/mixins/useTableColumns";

export default {
  name: 'new-groups',
  components: {
    GroupMobileFilterForm,
    EditGroupForm,
    MergeGroupsForm,
    CopyGroup,
    DeleteGroupForm,
    NewOrderForm,
    ManageAccessForm,
    SearchOutlined, SettingOutlined, PlusOutlined, ReloadOutlined, MenuOutlined, DownOutlined},
  setup() {

    const store = useStore(),
        router = useRouter();

    const showOrderForm = ref(false);
    const actionToApply = ref();
    const tableLoading = ref(true);
    const showActionModal = ref();
    const currentAction = ref();
    const modalActionLoading = ref(false);
    const statusError = ref({
      groupName: null,
    });

    const actionButtonLabels = {
      copy: 'Копировать',
      delete: 'Удалить',
      merge: 'Объединить',
      create: 'Создать'
    }

    const showMobileFilterModal = ref(false);

    const selectedGroups = ref([]);
    const groups = computed(() => store.state.group.groups);
    const groupsTotalCount = computed(() => store.state.group.totalGroupPageCount);
    const groupData = computed(() => {
      return groups.value.map((group) => {
        return {
          id: group.id,
          name: group.name,
          users_count: group.users_count,
          created_at: group.created_at,
          updated_at: group.updated_at,
        };
      })
    });

    const manageGroupAccessUsers = ref([]);

    const currentActionGroup = ref();

    const showFilterModal = ref(false);

    const entitiesPerPage = ref(10);
    const currentPage = ref(1);
    const totalUsers = computed(() => store.state.users.totalUsers);

    const filtersPopoverOpen = ref(false);

    const initialColumns = ref([
      {
        title: 'ID',
        dataIndex: 'id',
        width: 50,
        sorter: true,
        visible: true,
      },
      {
        title: 'Название',
        dataIndex: 'name',
        sorter: true,
        visible: true,
        width: 250,
      },
      {
        title: 'Участники',
        dataIndex: 'users_count',
        sorter: true,
        visible: true,
        width: 70,
      },
      {
        title: 'Создана',
        dataIndex: 'created_at',
        sorter: true,
        visible: true,
        width: 120,
      },
      {
        title: 'Изменена',
        dataIndex: 'updated_at',
        sorter: true,
        visible: true,
        width: 120,
      },
    ]);

    const { columns, scrollX } = useTableColumns(initialColumns, {tableKey: 'new-groups', settingsWidth: 40})

    const actions = [
      {
        id: 'merge',
        name: 'Объединить'
      },
      {
        id: 'delete',
        name: 'Удалить'
      },
      {
        id: 'export',
        name: 'Экспортировать'
      },
    ];
    const filters = computed(() => store.state.group.filters);
    const currentFiltersLine = computed(() => store.state.group.filterString);

    const fetchGroups = () => {
      tableLoading.value = true;

      store.dispatch("group/searchGroups", {
        per_page: entitiesPerPage.value,
        page: currentPage.value,
        sort_field: sortState.value.field,
        sort_direction:
            sortState.value.order === 'ascend'
                ? 'asc'
                : sortState.value.order === 'descend'
                    ? 'desc'
                    : null,
        ...filters.value
      }).then(() => {
        tableLoading.value = false;
      });
    }

    const onSelectChange = (selectedRowKeys) => {
      selectedGroups.value = selectedRowKeys;
    };

    const showModal = (action, groupData = null) => {
      if (groupData) {
        currentActionGroup.value = groupData;
      } else {
        currentActionGroup.value = null;
      }

      if (action === 'export') {
        exportGroups(currentActionGroup.value ? [currentActionGroup.value.id] : selectedGroups.value);
        return;
      }

      if (action === 'manage_access') {
        groups.value.map((group) => {
          if (groupData) {
            if (group.id === groupData.id || (!groupData && selectedGroups.value.find(group.id))) {
              group.users.map((user) => {
                if (!manageGroupAccessUsers.value.find((el) => el === user.id)) {
                  manageGroupAccessUsers.value.push(user.id);
                }
              })
            }
          }
        });
      }

      currentAction.value = action;
      showActionModal.value = true;
    }

    const exportGroups = (data) => {
      store.dispatch('group/exportGroups', {
        groups: data,
      }).finally(() => {
        clearCurrentAction()
      });

      message.info('Файл генерируется, пожалуйста, подождите')
    }

    // фильтры
    const openFilterModal = () => {
      showFilterModal.value = true;
    }

    const updateFilters = () => {
      filtersPopoverOpen.value = false;
      showMobileFilterModal.value = false;

      currentPage.value = 1;
      fetchGroups();
    }

    const resetFilters = () => {
      store.dispatch('group/setGroupFilters', {
        filters: {},
        filterString: null,
      });
    }

    //сортировка
    const sortState = ref({
      field: null,
      order: null,
    });

    const handleTableChange = (pagination, filter, sorter) => {
      sortState.value = {
        field: sorter?.field ?? null,
        order: sorter?.order ?? null,
      };

      fetchGroups();
    }

    // пагинация
    const retrieveEntitiesPerPage = () => {
      let storageValue = localStorage.getItem('perPage');
      entitiesPerPage.value = storageValue ? Number(storageValue) : 10;
    }

    const setEntitiesPerPage = (num) => {
      localStorage.setItem('perPage', num)
    }

    const handlePageChange = (current, perPage) => {
      if (entitiesPerPage.value !== perPage) {
        entitiesPerPage.value = perPage;
        setEntitiesPerPage(entitiesPerPage.value);
      }

      currentPage.value = current;
      fetchGroups();
    }

    const isMobile = ref(window.innerWidth < 768)

    const onResize = () => {
      isMobile.value = window.innerWidth < 768
    }

    onMounted(() => {
      window.addEventListener('resize', onResize)
    });


    const clearCurrentAction = () => {
      currentActionGroup.value = null;
      currentAction.value = null;
      actionToApply.value = null;

      showActionModal.value = false;
      manageGroupAccessUsers.value = [];
    }

    const fetchAfterSubmit = () => {
      clearCurrentAction();
      fetchGroups();
    }

    onBeforeMount(() => {
      retrieveEntitiesPerPage();
      fetchGroups();
    });

    onBeforeUnmount(() => {
      store.dispatch('group/clearGroupFilters');
    });

    watch(filters, () => {
      updateFilters();
      },
        { deep: true, immediate: true }
    );

    return {
      router,
      NotFoundIcon,
      currentPage,
      entitiesPerPage,
      columns,
      actionToApply,
      actions,
      groups,
      selectedGroups,
      onSelectChange,
      currentFiltersLine,
      tableLoading,
      showFilterModal,
      openFilterModal,
      updateFilters,
      filtersPopoverOpen,
      currentActionGroup,
      totalUsers,
      handlePageChange,
      showMobileFilterModal,
      handleTableChange,
      groupData,
      groupsTotalCount,
      isMobile,
      showActionModal,
      currentAction,
      modalActionLoading,
      statusError,
      actionButtonLabels,
      clearCurrentAction,
      showModal,
      manageGroupAccessUsers,
      showOrderForm,
      fetchAfterSubmit,
      resetFilters,
      scrollX
    }
  }
}
</script>

<style scoped lang="scss">
.card {
  min-height: 75vh;
}

.view-container {
  padding-top: 0;
  padding-bottom: 0;
}

:deep(.card .ant-table-content) {
  min-height: 60vh;
}

.filter-block {
  display: flex;
  column-gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  row-gap: 15px;
}

:deep(.ant-table-wrapper .ant-table-thead >tr>th) {
  padding: 5px;
}

:deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td) {
  padding: 10px 16px !important;
}

.action-select-wrapper {
  min-width: 180px;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;

    .ant-select {
      width: 100%;
    }
  }
}

.action-checkbox-wrapper {
  min-width: 200px;
}

.no-data-text {
  color: var(--main-font);
}

.add-button {
  @media (max-width: 550px) {
    width: 100%;

    button {
      width: 100%;
    }
  }
}

.search-tool-block {
  @media (max-width: 550px) {
    width: 100%;
  }
}

.search-tool-block .ant-select {
  width: 350px;

  @media (max-width: 550px) {
    width: 100%;
  }
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 0;
    display: flex;
    column-gap: 25px;
    align-items: center;
    justify-content: space-between;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
}

.extended-search-block {
  display: flex;
  align-items: center;
  column-gap: 10px;
  width: 300px;

  @media (max-width: 768px) {
    display: none;
  }
}

.extended-search-block-mobile {
  display: none;
  margin-right: 10px;

  @media(max-width: 768px) {
    display: block;
  }
}

.delete-checkbox-wrapper {
  margin-top: 10px;
}

:deep(.anticon-reload) {
  cursor: pointer;
}

:deep(.anticon-reload:hover) {
  color: var(--main-green) !important;
}

.add-button button {
  background-color: var(--main-green) !important;
  color: #fff;
}

.add-button button:hover {
  color: #fff;
  border-color: var(--polar-green-6);
}

.no-data-wrapper {
  height: 50vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 5px;
  padding: 24px;
}

.no-data-wrapper button {
  margin-top: 10px;
}

.no-data-wrapper button:hover {
  border-color: var(--main-green);
  color: var(--main-green);
}

.select-users-wrapper {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  row-gap: 5px;
}

.select-users {
  width: 100%;
}

@media (max-width: 768px) {
  :deep(.ant-pagination .ant-pagination-total-text ) {
    display: none !important;
  }

  .action-buttons .ant-row {
    flex-direction: column;
    row-gap: 10px;
  }

  .action-buttons .ant-row button{
    width: 100%
  }
}

:deep(.ant-input-focused) {
  border-color: var(--main-green);
}

.ant-input:hover,
.ant-input:focus {
  border-color: var(--main-green);;
}

.ant-input:focus,
.ant-select-focused .ant-select-selector {
  box-shadow: 0 0 0 2px var(--polar-green);
}

.ant-input-affix-wrapper:hover,
.ant-input-affix-wrapper-focused {
  border-color: var(--main-green);
}

.ant-input-affix-wrapper-focused {
  box-shadow: 0 0 0 2px var(--polar-green);
}

.filter-button:not(:disabled) {
  background-color: var(--main-green);
  color: #fff;
}

.filter-button:not(:disabled):hover {
  background-color: var(--main-green);
  border-color: var(--polar-green-6);
  color: #fff;
}

.filter-button:active,
.add-button button:active {
  background-color: var(--main-green);
  border-color: var(--polar-green-6);
  color: #fff;
}

.filter-cancel-button:hover,
.filter-cancel-button:active {
  color: var(--dist-red-main);
  border-color: var(--dist-red-main);
}

.search-outline-block {
  position: relative;
}

.dot {
  position: absolute;
  top: -1px;
  left: 11px;
  height: 5px;
  width: 5px;
  background-color: var(--color-error);
  border-radius: 50%;
}

.group-link {
  color: var(--main-green);
}

.group-link:hover {
  color: var(--polar-green-3);
}

:deep(.ant-btn-text.ant-dropdown-trigger) {
  color: lightgrey;
}

:deep(.ant-btn-text.ant-dropdown-trigger.ant-dropdown-open) {
  color: var(--main-font);
}

:deep(.ant-table-tbody .ant-dropdown-trigger) {
  padding: 4px 5px !important;
}

:deep(.ant-table-wrapper .ant-table-tbody >tr.ant-table-row-selected >td) {
  background-color: var(--light-grey);
}

:deep(.ant-table-wrapper .ant-table-tbody >tr.ant-table-row-selected:hover>td) {
  background-color: var(--light-grey);
}

:deep(.ant-divider-horizontal) {
  margin: 16px 0 !important;
}

h2 {
  font-weight: 600;
  font-size: 16px;
}

:deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td:has(.ant-dropdown-trigger)),
:deep(.ant-table-wrapper .ant-table-tbody>tr:not(.ant-table-measure-row)>td:has(.ant-checkbox-wrapper)) {
  padding: 5px !important;
}
</style>